import { GTMProvider as ReactGTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { ISnippetsParams } from '@elgorditosalsero/react-gtm-hook/dist/models/GoogleTagManager';
import { CircularProgress, CssBaseline, Stack, ThemeProvider /*useMediaQuery*/, useMediaQuery } from '@mui/material';
import useExperiments from 'api/experiments';
import Flags from 'api/feature-flags';
import { useReportGuids } from 'api/hooks/useEnvGuid';
import AuthenticatedRoute from 'containers/authenticated-router';
import getPublicRoutes from 'containers/public-router/routes';
import React, { createContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { theme } from 'theme';
import { theme as newUiTheme, themeDark as newUiThemeDark } from 'theme/newUi';
import { ControllerState } from 'utils';

import { AppRoutes } from 'config/app-routes';
import { AuthProvider } from 'hooks/useAuth';
import { NotificationProvider } from 'hooks/useNotify';
import NotFound from 'pages/404';
import getAdminRoutes from 'pages/admin/routes';
import getDashboardRoutes from 'pages/dashboard2/routes';
import getDataExportRoutes from 'pages/data-export/routes';
import getExploreRoutes from 'pages/explore/routes';
import getInsightsRoutes from 'pages/insights2/routes';
import getInternalRoutes from 'pages/internal/routes';
import getKnowledgeBaseRoutes from 'pages/knowledge-base/routes';
import getNotificationRoutes from 'pages/notifications/routes';
import getPrivacyRoutes from 'pages/privacy/routes';
import getRuleConfigurationRoutes from 'pages/rule-configuration/routes';
import getTechnologyRoutes from 'pages/technology/routes';
import { AxiosProvider } from 'utils/transport/axios.context';

const THEME_MODE_PARAM = 'themeMode';
const THEME_VERSION_PARAM = 'themeVersion';

function getQueryParameter(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return new URLSearchParams(window.location.search).get(name);
}

function getInitialThemeVersion(): ThemeContextType['version'] {
  const versionFromQuery = getQueryParameter(THEME_VERSION_PARAM);
  if (versionFromQuery === 'newUi' || versionFromQuery === 'default') {
    return versionFromQuery;
  }

  if (typeof window === 'undefined') {
    return 'default';
  }

  return window.localStorage.getItem('theme-version') === 'newUi' ? 'newUi' : 'default';
}

function getInitialThemeMode(): ThemeContextType['mode'] {
  const modeFromQuery = getQueryParameter(THEME_MODE_PARAM);
  if (modeFromQuery === 'light' || modeFromQuery === 'dark' || modeFromQuery === 'system') {
    return modeFromQuery;
  }

  if (typeof window === 'undefined') {
    return 'system';
  }

  const storedMode = window.localStorage.getItem('theme-mode');
  if (storedMode === 'light' || storedMode === 'dark') {
    return storedMode;
  }

  return 'system';
}

const gtmParams: ISnippetsParams = {
  id: process.env.REACT_APP_GTM_ID ?? 'GTM-58BS597',
  environment: {
    gtm_auth: process.env.REACT_APP_GTM_AUTH ?? '',
    gtm_preview: process.env.REACT_APP_GTM_PREVIEW ?? '',
  },
};

const lazily = (page: string) => {
  return React.lazy(() => import(`pages/${page}`));
};

const Dashboard = lazily('dashboard');
const InsightsPage = lazily('insights');

const GTMProvider = process.env.NODE_ENV === 'production' ? ReactGTMProvider : (_: any) => <>{_.children}</>;

const themeMap = {
  default: {
    light: theme,
    dark: theme,
  },
  newUi: {
    light: newUiTheme,
    dark: newUiThemeDark,
  },
} as const;

type ThemeContextType = {
  version: 'default' | 'newUi';
  mode: 'light' | 'dark' | 'system';
};

export const ThemeContext = createContext(
  {} as {
    version: ControllerState<ThemeContextType['version']>;
    mode: ControllerState<ThemeContextType['mode']>;
  }
);

const App = () => {
  const [version, setVersion] = useState<ThemeContextType['version']>(() => getInitialThemeVersion());
  const [mode, setMode] = useState<ThemeContextType['mode']>(() => getInitialThemeMode());
  const systemDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { isExperimentEnabled } = useExperiments();

  const _realMode = mode === 'system' ? (systemDarkMode ? 'dark' : 'light') : mode;
  const realMode = version === 'default' ? 'light' : _realMode;
  document.documentElement.setAttribute('theme', realMode);

  useEffect(() => {
    const queryVersion = getQueryParameter(THEME_VERSION_PARAM);
    if (queryVersion === 'newUi' || queryVersion === 'default') {
      window.localStorage.setItem('theme-version', queryVersion);
    }

    const queryMode = getQueryParameter(THEME_MODE_PARAM);
    if (queryMode === 'light' || queryMode === 'dark' || queryMode === 'system') {
      window.localStorage.setItem('theme-mode', queryMode);
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{ version: { current: version, onChange: setVersion }, mode: { current: mode, onChange: setMode } }}
    >
      <ThemeProvider theme={themeMap[version][realMode]}>
        <NotificationProvider>
          <CssBaseline enableColorScheme={version === 'newUi' && mode === 'dark'} />
          <Router>
            <GTMProvider state={gtmParams}>
              <AxiosProvider>
                <AuthProvider>
                  <Routes>
                    {/* Authenticated routes automatically redirect to a login page if not logged in */}
                    <Route path="/" element={<AuthenticatedRoute />}>
                      {getAdminRoutes(lazily, isExperimentEnabled)}
                      {getPrivacyRoutes(lazily, isExperimentEnabled)}
                      {getInternalRoutes(lazily, isExperimentEnabled)}
                      {getTechnologyRoutes(lazily, isExperimentEnabled)}
                      {getNotificationRoutes(lazily, isExperimentEnabled)}
                      {getExploreRoutes(lazily, isExperimentEnabled)}
                      {getKnowledgeBaseRoutes(lazily, isExperimentEnabled)}
                      {getDataExportRoutes(lazily, isExperimentEnabled)}
                      {getRuleConfigurationRoutes(lazily, isExperimentEnabled)}
                      {getDashboardRoutes(lazily, (id: string) => Flags.isFlagEnabled(id))}
                      {getInsightsRoutes(lazily, (id: string) => Flags.isFlagEnabled(id))}
                      {/* Change later */}
                      <Route path={AppRoutes.DASHBOARD} element={<Dashboard />} />
                      <Route path={AppRoutes.CONSENT_DASHBOARD + '*'} element={<ConsentDashboardNavigate />} />
                      <Route path={AppRoutes.INSIGHTS} element={<InsightsPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>

                    {getPublicRoutes(lazily, isExperimentEnabled)}
                  </Routes>
                </AuthProvider>
              </AxiosProvider>
            </GTMProvider>
          </Router>
        </NotificationProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

function ConsentDashboardNavigate() {
  const { envGuid } = useReportGuids();
  const isIframe = window.self !== window.top;
  const pathname = window.location.pathname.replace(AppRoutes.CONSENT_DASHBOARD, '');
  if (isIframe) {
    window.top?.postMessage(
      {
        event: 'parent-navigate',
        data: pathname,
      },
      window.location.origin
    );
  } else {
    const url = `${window.location.protocol}//consent-${window.location.host}${pathname}?environment=${envGuid}`;
    window.location.href = url;
  }

  return (
    <Stack alignItems="center" justifyContent="center" flexGrow={1}>
      <CircularProgress size={32} />
    </Stack>
  );
}

export type RouteListGetter = (
  lazily: (page: string) => React.LazyExoticComponent<React.ComponentType<any>>,
  isExperimentEnabled: (experiment: string) => boolean
) => JSX.Element;

export default App;
